import React from 'react';
import RowsPerPageSelect from '../../components/shared/RowsPerPageSelect';

const SIBLINGS = 1;

const range = (from, to) => Array.from({ length: to - from + 1 }, (_, i) => from + i);

// Builds a compact page list like [1, '…', 5, 6, 7, '…', 116] so large
// catalogs don't render a button for every page. Always returns the same
// number of slots so Previous/Next don't jump around while paging.
function getPageItems(page, totalPages) {
  // first + last + current + siblings + two ellipses
  const maxSlots = SIBLINGS * 2 + 5;
  if (totalPages <= maxSlots) return range(1, totalPages);

  // pages shown next to first/last when the current page is near an edge
  const edgeCount = SIBLINGS * 2 + 3;

  if (page <= edgeCount - SIBLINGS) {
    return [...range(1, edgeCount), 'ellipsis-right', totalPages];
  }
  if (page >= totalPages - edgeCount + SIBLINGS + 1) {
    return [1, 'ellipsis-left', ...range(totalPages - edgeCount + 1, totalPages)];
  }
  return [
    1,
    'ellipsis-left',
    ...range(page - SIBLINGS, page + SIBLINGS),
    'ellipsis-right',
    totalPages,
  ];
}

export default function Pagination({
  page,
  totalPages,
  total,
  setPage,
  pageSize,
  pageSizeOptions,
  onPageSizeChange,
}) {
  // Shown whenever the catalogue outgrows the smallest page, not just when it
  // spans pages, so a larger page size can always be switched back.
  if (total <= pageSizeOptions[0]) return null;

  const items = getPageItems(page, totalPages);

  return (
    <div className="pagination-bar">
      <div className="pagination-controls">
        <button
          onClick={() => setPage(Math.max(page - 1, 1))}
          disabled={page === 1}
          className="pagination-button"
        >
          Previous
        </button>

        {items.map((item) =>
          typeof item === 'number' ? (
            <button
              key={item}
              onClick={() => setPage(item)}
              className={`pagination-button ${page === item ? 'active' : ''}`}
              aria-current={page === item ? 'page' : undefined}
            >
              {item}
            </button>
          ) : (
            <span key={item} className="pagination-ellipsis">
              …
            </span>
          )
        )}

        <button
          onClick={() => setPage(Math.min(page + 1, totalPages))}
          disabled={page === totalPages}
          className="pagination-button"
        >
          Next
        </button>

        <span className="pagination-summary">
          Page {page} of {totalPages}
        </span>
      </div>
      <RowsPerPageSelect value={pageSize} options={pageSizeOptions} onChange={onPageSizeChange} />
    </div>
  );
}
